import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { CreditCard, Lock, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { getErrorMessage } from '../utils/apiError';

import { BACKEND_URL } from '../config'  // Auto-fixed: use centralized config;
const API_BASE = `${BACKEND_URL}/api`;

/**
 * Instant-booking payment form.
 *
 * Upgraded from the legacy CardElement (card-only) to PaymentElement using
 * Stripe's DEFERRED-INTENT flow: the PaymentIntent (and the speculative
 * booking behind it) is still only created when the guest presses Pay —
 * same timing as before, so no phantom bookings from abandoned modals —
 * but the guest now sees Apple Pay / Google Pay / Link alongside cards.
 *
 * Requires the parent <Elements> provider to pass
 * `options={{ mode: 'payment', amount, currency: 'cad' }}` (PaymentModal
 * does). The amount passed there MUST equal the intent amount the backend
 * creates, or Stripe rejects the confirm.
 */
const CreditCardForm = ({ 
  amount, 
  propertyId, 
  propertyTitle,
  checkIn, 
  checkOut, 
  guests,
  customerName,
  customerEmail,
  customerPhone,
  message,
  onSuccess,
  onError,
  buttonText = 'Pay Now',
  // Pricing breakdown (source of truth – must match Stripe-charged amount)
  pricePerNight = null,
  accommodationSubtotal = null,
  discountAmount = 0,
  bookingType = 'full_property',
  roomNumber = null,
  roomName = null,
  // Parking add-on — optional. When requested, the backend re-verifies
  // availability + pricing server-side and adds the charge to the PaymentIntent.
  parkingRequested = false,
  parkingCarSize = null,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [saveCard, setSaveCard] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1 (deferred-intent flow): validate the PaymentElement inputs
      // BEFORE creating the intent — bad card details never hit the backend.
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setLoading(false);
        return;
      }

      // Normalize guests to total int (defensive – PaymentModal already sums adults+children)
      const totalGuests = (typeof guests === 'object' && guests !== null)
        ? (Number(guests.adults) || 0) + (Number(guests.children) || 0)
        : Number(guests) || 1;

      // Step 2: Create payment intent on backend with customer info from props
      const userToken = localStorage.getItem('user_token') || localStorage.getItem('token');
      const response = await axios.post(`${API_BASE}/payments/create-payment-intent`, {
        amount: amount,
        property_id: propertyId,
        check_in: checkIn,
        check_out: checkOut,
        guests: totalGuests,
        save_card: saveCard,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        booking_type: bookingType,
        room_number: roomNumber,
        // Parking add-on fields consumed by /payments/create-payment-intent.
        // The backend calls routes.parking.pick_cheapest_slot to verify the
        // price and assign a slot; the car size here is the only guest choice.
        parking_requested: !!parkingRequested,
        parking_car_size: parkingCarSize || null,
        metadata: {
          property_title: propertyTitle,
          customer_name: customerName,
          customer_email: customerEmail,
          message: message || '',
          // CRITICAL: Pass frontend-computed pricing so backend saves the SAME values Stripe charged
          ...(pricePerNight != null && { price_per_night: String(pricePerNight) }),
          ...(accommodationSubtotal != null && { accommodation_subtotal: String(accommodationSubtotal) }),
          ...(discountAmount != null && { discount_amount: String(discountAmount) }),
          ...(roomName && { room_name: roomName })
        }
      }, {
        headers: userToken ? { Authorization: `Bearer ${userToken}` } : {},
      });

      const { client_secret, payment_intent_id } = response.data;

      // Step 3: Confirm with the PaymentElement (card, Apple Pay, Google
      // Pay, Link — whatever the guest picked). `redirect: 'if_required'`
      // keeps card + wallet payments on-page; only redirect-based methods
      // (if ever enabled in the Stripe dashboard) would navigate away.
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: client_secret,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: customerName,
              email: customerEmail,
              phone: customerPhone,
            },
          },
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        setError(stripeError.message);
        toast({
          title: 'Payment failed',
          description: stripeError.message,
          variant: 'destructive',
        });
        // Roll back the speculative booking the backend created in
        // /payments/create-payment-intent. The Stripe webhook handles this
        // too, but firing the rollback here removes the lag where the
        // customer briefly sees a "confirmed" booking on their dashboard.
        if (payment_intent_id) {
          axios.post(
            `${API_BASE}/payments/payment-intent/${payment_intent_id}/mark-failed`,
            {
              failure_code: stripeError.code || 'card_declined',
              failure_message: stripeError.message,
            },
          ).catch((rollbackErr) => {
            console.warn('Booking rollback hint failed (webhook will catch up):', rollbackErr?.message);
          });
        }
        if (onError) onError(stripeError);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        toast({
          title: 'Payment successful!',
          description: 'Your booking has been confirmed.',
        });
        // Payment succeeded - call success handler directly
        // No need to redirect to success page for card payments
        if (onSuccess) onSuccess(paymentIntent);
      }
    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = getErrorMessage(err, 'Payment failed');
      setError(errorMessage);
      toast({
        title: 'Payment failed',
        description: errorMessage,
        variant: 'destructive',
      });
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentChange = (event) => {
    setPaymentComplete(event.complete);
    setError(null);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="instant-booking-payment-form">
      {/* Payment method — card / Apple Pay / Google Pay / Link */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Payment Method
          </div>
        </label>

        <div className="border border-gray-300 rounded-lg p-4 bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all">
          <PaymentElement
            options={{
              layout: 'tabs',
              defaultValues: { billingDetails: { address: { country: 'CA' } } },
            }}
            onChange={handlePaymentChange}
          />
        </div>
        {error && (
          <p className="mt-2 text-sm text-red-600 flex items-center gap-1" data-testid="payment-error-message">
            <span className="font-medium">Error:</span> {error}
          </p>
        )}
      </div>

      {/* Save Card Option */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="saveCard"
          checked={saveCard}
          onChange={(e) => setSaveCard(e.target.checked)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          data-testid="save-card-checkbox"
        />
        <label htmlFor="saveCard" className="text-sm text-gray-700 cursor-pointer">
          Save card for future bookings
        </label>
      </div>

      {/* Security Notice */}
      <Alert className="bg-gray-50 border-gray-200">
        <Lock className="h-4 w-4" />
        <AlertDescription className="text-xs text-gray-600">
          Your payment is secured by Stripe. Card details are encrypted and never stored on our servers.
          We never see your full card number.
        </AlertDescription>
      </Alert>

      {/* Amount Display */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-blue-900">Total Amount:</span>
          <span className="text-2xl font-bold text-blue-600" data-testid="payment-total-amount">
            ${(amount).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={!stripe || !paymentComplete || loading}
        className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base font-semibold"
        data-testid="instant-booking-pay-button"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Processing Payment...
          </>
        ) : (
          <>
            <Lock className="w-5 h-5 mr-2" />
            {buttonText}
          </>
        )}
      </Button>

      <p className="text-xs text-center text-gray-500">
        By confirming your payment, you agree to our Terms of Service and Privacy Policy.
      </p>
    </form>
  );
};

export default CreditCardForm;
